import React, { useState, useEffect, useRef } from 'react';
import MMAllegroSetupBanner from './MMAllegroSetupBanner';
import { getAuthToken } from './authToken';
import { useShortcuts } from './useShortcuts';
import OfferPicker from './OfferPicker';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import AIOpen from './AIOpen';
import DictateButton from './DictateButton';
import Shipments from './Shipments';
import Orders from './Orders';
import ShortcutSuggestions from './ShortcutSuggestions';
import axios from 'axios';
import { OrderNoBadge } from './orderNumberPref';
import Settings from './Settings';
import Login from './Login';
import Ratings from './Ratings';
import AppMobile from './AppMobile';
import Issues from './Issues';
import Returns from './Returns';
import BuyerReturnsButton from './BuyerReturnsButton';
import BuyerShipmentsButton from './BuyerShipmentsButton';
import AssignButton from './AssignButton';
import EmailInbox from './EmailInbox';
import Offers from './Offers';
import ApiloModule from './ApiloModule';
import RefundClaims from './RefundClaims';
import Home from './Home';
import Users from './Users';
import AIAssistant from './AIAssistant';
import LiveAlertBar from './LiveAlertBar';

const API = '/api';

interface Thread {
  id: string;
  read: boolean;
  lastMessageDateTime: string;
  interlocutor: { login: string; avatarUrl: string; };
  account_id?: string;
  account_name?: string;
  has_attachments?: boolean;
  requires_reply?: boolean;
  language?: string;
  auto_reply_disabled?: boolean;
  assigned_to?: string | null;
  is_handled?: boolean;
}

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  url: string;
  proxy_url?: string;
}

interface ThreadMessage {
  id: string;
  text: string;
  author: { login: string; };
  createdAt: string;
  attachments?: Attachment[];
}

interface AIDraft {
  draft: string;
  confidence: number;
}

const LANG_FLAGS: {[key:string]:string} = {
  'en': '🇬🇧', 'de': '🇩🇪', 'cs': '🇨🇿', 'sk': '🇸🇰',
  'ro': '🇷🇴', 'hu': '🇭🇺', 'uk': '🇺🇦', 'ru': '🇷🇺',
  'fr': '🇫🇷', 'it': '🇮🇹', 'es': '🇪🇸', 'nl': '🇳🇱',
  'bg': '🇧🇬', 'el': '🇬🇷', 'sv': '🇸🇪', 'lt': '🇱🇹',
  'lv': '🇱🇻', 'et': '🇪🇪',
};

function decodeHtml(html: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

function toLocalDate(d: string): Date {
  if (!d) return new Date();
  const s = d.replace(' ', 'T');
  const withZ = !s.includes('Z') && !s.includes('+') ? s + 'Z' : s;
  return new Date(withZ);
}

function formatDate(d: string): string {
  if (!d) return '';
  const date = toLocalDate(d);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  if (diff < 86400000) return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatDateTime(d: string): string {
  if (!d) return '';
  const date = toLocalDate(d);
  return date.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function App() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selected, setSelected] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [reply, setReply] = useState('');
  const replyRef = useRef<HTMLDivElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [showOfferPicker, setShowOfferPicker] = useState(false);
  const [showAIOpen, setShowAIOpen] = useState(false);
  const [showShipments, setShowShipments] = useState(false);
  const [showOrdersModule, setShowOrdersModule] = useState(false);

  // Otwieranie modulu z parametrow URL (uzywane przez ramke w module Przesylki)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const open = p.get('open');
    const login = p.get('login') || '';
    if (!open) return;
    if (open === 'messages') {
      if (login) setSearch(login);
      setFilter('all');
      setShowHome(false);
      setTimeout(() => { try { fetchThreads(0, 'all', login); } catch (e) {} }, 400);
    } else if (open === 'issues') {
      if (login) localStorage.setItem('issues_prefill_search', login);
      localStorage.setItem('issues_prefill_filter', 'all');
      setShowIssues(true);
      setShowHome(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A2: powrot z OAuth Allegro do modulu z ktorego klient klikal baner + toast potwierdzenia
  useEffect(() => {
    let rm = '';
    try { rm = localStorage.getItem('mm_return_module') || ''; } catch (e) {}
    if (!rm) return;
    // NIE usuwamy mm_return_module — skonsumuje go baner Allegro (pokaze ekran gratulacji).
    setShowHome(false);
    // Bezposrednie settery (NIE handleModule — jest zadeklarowane nizej, TDZ → bialy ekran).
    if (rm === 'ratings') { setShowRatings(true); localStorage.setItem('assistant_context', 'ratings'); }
    else if (rm === 'issues') { setShowIssues(true); localStorage.setItem('assistant_context', 'issues'); }
    else if (rm === 'offers') { setShowOffers(true); localStorage.setItem('assistant_context', 'offers'); }
    else if (rm === 'returns') { setShowReturns(true); localStorage.setItem('assistant_context', 'returns'); }
    else if (rm === 'refund-claims') { setShowRefundClaims(true); localStorage.setItem('assistant_context', 'refund-claims'); }
    else if (rm === 'shipments') { setShowShipments(true); localStorage.setItem('assistant_context', 'shipments'); }
    else if (rm === 'messages') { /* widok wiadomosci = domyslny po zejsciu z Home */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { showSuggestions: showReplySuggestions, suggestions: replySuggestions, handleChange: handleReplyChange, applyShortcut: applyReplyShortcut, setShowSuggestions: setShowReplySuggestions } = useShortcuts();
  const [aiDraft, setAiDraft] = useState<AIDraft | null>(null);
  const [replyIsManual, setReplyIsManual] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [copyToast, setCopyToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentLearned, setSentLearned] = useState(false);
  const [filter, setFilter] = useState('unread');
  const [classification, setClassification] = useState<any>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showRatings, setShowRatings] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [showReturns, setShowReturns] = useState(false);
  const [returnsOverdueFilter, setReturnsOverdueFilter] = useState(false);
  const [returnsOverdueNonce, setReturnsOverdueNonce] = useState(0);
  const [openReturnRef, setOpenReturnRef] = useState('');
  const [openReturnNonce, setOpenReturnNonce] = useState(0);
  const [openShipmentOrderId, setOpenShipmentOrderId] = useState("");
  const [openShipmentAccountId, setOpenShipmentAccountId] = useState("");
  const [openShipmentNonce, setOpenShipmentNonce] = useState(0);
  const [showEmail, setShowEmail] = useState(false);
  const [showOffers, setShowOffers] = useState(false);
  const [showApilo, setShowApilo] = useState(false);
  const [showRefundClaims, setShowRefundClaims] = useState(false);
  const [showHome, setShowHome] = useState(true);
  const [showUsers, setShowUsers] = useState(false);

  const [negativeRatings, setNegativeRatings] = useState(0);
  const [expiredAccounts, setExpiredAccounts] = useState<string[]>([]);
  const isAccountExpired = (accountName: string) => expiredAccounts.includes(accountName);
  const [sortBy, setSortBy] = useState<'date'|'account'>('date'); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [filterAccount, setFilterAccount] = useState<string>('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const [totalThreads, setTotalThreads] = useState(0);
  const [accounts, setAccounts] = useState<{id:string,name:string}[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroMsgBannerClosed, setAllegroMsgBannerClosed] = useState(false);
  const [msgJustReturnedFromAllegro] = useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'messages'; } catch (e) { return false; }
  });
  useEffect(() => {
    if (msgJustReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [msgJustReturnedFromAllegro]);
  const [selected_ids, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [token, setToken] = useState<string>(localStorage.getItem('token') || '');
  const [syncWarning, setSyncWarning] = useState(false);
  const [userReady, setUserReady] = useState<boolean>(!localStorage.getItem('token'));
  const [showMsgHistory, setShowMsgHistory] = useState(false);
  const [msgHistory, setMsgHistory] = useState<any[]>([]);
  const [msgHistoryLoading, setMsgHistoryLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<{name:string,email:string,role:string,modules:string[],can_assign?:boolean,color?:string} | null>(
    (() => { try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { localStorage.removeItem('user'); return null; } })()
  );
  const [lockInfo, setLockInfo] = useState<string | null>(null);
  const [threadLang, setThreadLang] = useState('pl');
  const [offerInfo, setOfferInfo] = useState<any>(null);
  const [showOrders, setShowOrders] = useState(false);
  const [buyerOrders, setBuyerOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessageForm, setNewMessageForm] = useState({ account_id: '', recipient_login: '', order_id: '', text: '', language: 'pl' });
  const [newMessageOrders, setNewMessageOrders] = useState<any[]>([]);
  const [newMessageLoadingOrders, setNewMessageLoadingOrders] = useState(false);
  const [newMessageSending, setNewMessageSending] = useState(false);
  const [newMessageAttachments, setNewMessageAttachments] = useState<{id: string, filename: string}[]>([]);
  const [newMessageUploadingFile, setNewMessageUploadingFile] = useState(false);
  const [newMessageAiLoading, setNewMessageAiLoading] = useState(false);
  const [showNewMessageOfferPicker, setShowNewMessageOfferPicker] = useState(false);
  const newMessageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { showSuggestions: showNewMessageSuggestions, suggestions: newMessageSuggestions, handleChange: handleNewMessageChange, applyShortcut: applyNewMessageShortcut, setShowSuggestions: setShowNewMessageSuggestions } = useShortcuts();
  const [translations, setTranslations] = useState<{[key:string]:string}>({});
  const [showTranslation, setShowTranslation] = useState<{[key:string]:boolean}>({});
  const [replyTranslation, setReplyTranslation] = useState('');
  const [attachments, setAttachments] = useState<{id: string, filename: string}[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [translatingReply, setTranslatingReply] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lockHeartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/api/agents').then(r => { (window as any).__agents__ = r.data; }).catch(() => {});
      axios.interceptors.response.use(r => r, err => {
        if (err.response?.status === 401) {
          // WAZNE: pomijamy wylogowanie przy 401 z lock/unlock. Heartbeat locka (co 3 min)
          // moze wyslac zadanie ze starym/wygaslym tokenem z poprzedniej sesji (osierocony
          // setInterval), co nieslusznie kasowalo swiezy token i psulo sesje (znikajace zdjecia).
          // Lock/unlock to akcje pomocnicze — ich 401 NIE oznacza utraty sesji uzytkownika.
          const _url = err.config?.url || '';
          const _isLockCall = _url.includes('/lock') || _url.includes('/unlock');
          if (!_isLockCall) {
            // Prawdziwe wygasniecie sesji — pelne, czyste wylogowanie i powrot do ekranu logowania.
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
            setToken('');
            setCurrentUser(null);
          }
        }
        return Promise.reject(err);
      });
      axios.get('/auth/me').then(r => {
        const fresh = { ...r.data, modules: r.data.modules || [] };
        setCurrentUser(fresh);
        localStorage.setItem('user', JSON.stringify(fresh));
        setUserReady(true);
      }).catch(() => { setUserReady(true); });
      fetchThreads();
      const checkSync = () => {
        if (currentUser?.role === 'admin') {
          axios.get('/api/health/sync').then(r => {
            setSyncWarning(!r.data.ok);
          }).catch(() => {});
        }
      };
      checkSync();
      const syncInterval = setInterval(checkSync, 60000);
      const checkTokens = () => {
        axios.get(`${API}/allegro/accounts`).then(r => {
          setAccounts(r.data); setAccountsLoaded(true);
          const now = new Date();
          const expired = (r.data || []).filter((a: any) => a.token_invalid || (a.expires_at && new Date(a.expires_at.replace(' ', 'T') + (a.expires_at.includes('Z') || a.expires_at.includes('+') ? '' : 'Z')) < now)).map((a: any) => a.name);
          setExpiredAccounts(expired);
        }).catch(() => {});
      };
      checkTokens();
      const tokenInterval = setInterval(checkTokens, 30000);
      const checkNegative = () => {
        axios.get(`${API}/allegro/ratings?limit=50`).then(r => {
          const neg = (r.data.ratings || []).filter((x: any) => !x.recommended && !x.answer?.message);
          setNegativeRatings(neg.length);
        }).catch(() => {});
      };
      checkNegative();
      const interval = setInterval(checkNegative, 300000);
      return () => { clearInterval(interval); clearInterval(tokenInterval); clearInterval(syncInterval); };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchThreads = async (p?: number, f?: string, s?: string) => {
    const currentPage = p !== undefined ? p : page;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        offset: String(currentPage * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (filterAccount !== 'all') params.set('account_id', filterAccount);
      const currentFilter = f !== undefined ? f : filter;
      if (currentFilter === 'unread') params.set('filter_unread', 'true');
      const currentSearch = s !== undefined ? s : search;
      if (currentSearch.trim()) params.set('search', currentSearch.trim());
      const res = await axios.get(`${API}/allegro/messages?${params.toString()}`);
      setThreads(res.data.threads || []);
      setTotalThreads(res.data.total || 0);
      if (p !== undefined) setPage(p);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const selectThread = async (t: Thread) => {
    if (selected && selected.id !== t.id) {
      axios.post(`${API}/allegro/messages/${selected.id}/unlock`).catch(() => {});
      if (lockHeartbeatRef.current) { clearInterval(lockHeartbeatRef.current); lockHeartbeatRef.current = null; }
    }
    localStorage.setItem('assistant_context', 'messages:thread');
    setSelected(t);
    setLockInfo(null);
    setReply('');
    setSent(false);
    setMessages([]);
    setOfferInfo(null);
    setAiDraft(null);
    setReplyIsManual(false);
    setClassification(null);
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/allegro/messages/${t.id}?account_id=${t.account_id || ''}`);
      const msgs: ThreadMessage[] = res.data.messages || [];
      setOfferInfo(res.data.offer_info || null);
      msgs.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setMessages(msgs);
      setTimeout(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
      const detectedLang = res.data.detected_language || 'pl';
      setThreadLang(detectedLang);
      // Wczesniej wykonane tlumaczenia - pokaz od razu, bez ponownego tlumaczenia.
      // Endpoint czyta WYLACZNIE z cache (nie wola modelu), wiec jest szybki i darmowy.
      if (detectedLang !== 'pl' && msgs.length) {
        axios.post(`${API}/ai/translations-cached`, {
          items: msgs.map(m => ({ id: m.id, text: decodeHtml(m.text || ''), source_lang: detectedLang })),
        }).then(r => {
          const found = r.data?.translations || {};
          const ids = Object.keys(found);
          if (ids.length) {
            setTranslations(prev => ({ ...prev, ...found }));
            setShowTranslation(prev => {
              const next = { ...prev };
              ids.forEach(id => { next[id] = true; });
              return next;
            });
          }
        }).catch(() => {});
      }
      try {
        await axios.post(`${API}/allegro/messages/${t.id}/lock`);
        setLockInfo(null);
        // Heartbeat - odnawia lock co 3 minuty zeby nie wygasl po 10 min
        lockHeartbeatRef.current = setInterval(() => {
          axios.post(`${API}/allegro/messages/${t.id}/lock`)
            .then(() => setLockInfo(null))
            .catch(() => {});
        }, 3 * 60 * 1000);
      } catch (e: any) {
        if (e.response?.status === 423) {
          setLockInfo(e.response.data.detail);
        }
      }
      const clientMsgs = msgs.filter(m => m.author?.login === t.interlocutor.login);
      if (clientMsgs.length > 0) {
        const lastMsg = decodeHtml(clientMsgs[clientMsgs.length - 1].text || '');
        const classRes = await axios.post(`${API}/ai/classify`, { message: lastMsg });
        setClassification(classRes.data);
      }
    } catch (e) { console.error(e); setAiLoading(false); }
    setLoadingMessages(false);
  };

  const regenerateReply = async () => {
    if (!selected || messages.length === 0) return;
    const clientMsgs = messages.filter(m => m.author?.login === selected.interlocutor.login);
    if (clientMsgs.length === 0) return;
    const lastMsg = decodeHtml(clientMsgs[clientMsgs.length - 1].text || '');
    const history = messages.map(m => ({
      text: decodeHtml(m.text || '').substring(0, 500),
      is_client: m.author?.login === selected.interlocutor.login
    }));
    setAiLoading(true);
    try {
      const offerContext = offerInfo ? `Produkt którego dotyczy wiadomość: ${offerInfo.name} (ID: ${offerInfo.id}, URL: ${offerInfo.url})` : '';
      const res = await axios.post(`${API}/ai/generate-reply`, { message: lastMsg, history, account_id: selected.account_id || '', context: offerContext, hint: reply });
      setAiDraft(res.data);
      setReply(res.data.draft);
    } catch (e) { console.error(e); }
    setAiLoading(false);
  };

  const fetchBuyerOrders = async () => {
    if (!selected) return;
    setLoadingOrders(true);
    setShowOrders(true);
    try {
      const res = await axios.get(`${API}/allegro/buyer-orders`, {
        params: { buyer_login: selected.interlocutor.login, account_id: selected.account_id || '' }
      });
      setBuyerOrders(res.data.orders || []);
    } catch (e) { console.error(e); }
    setLoadingOrders(false);
  };
  const fetchNewMessageOrders = async () => {
    if (!newMessageForm.account_id || !newMessageForm.recipient_login.trim()) return;
    setNewMessageLoadingOrders(true);
    setNewMessageOrders([]);
    setNewMessageForm(prev => ({ ...prev, order_id: '' }));
    try {
      const res = await axios.get(`${API}/allegro/find-buyer-orders`, {
        params: { buyer_login: newMessageForm.recipient_login.trim(), account_id: newMessageForm.account_id }
      });
      const orders = res.data.orders || [];
      setNewMessageOrders(orders);
      if (orders.length === 1) setNewMessageForm(prev => ({ ...prev, order_id: orders[0].order_id }));
    } catch (e: any) {
      alert('Błąd wyszukiwania zamówień: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setNewMessageLoadingOrders(false);
  };
  const uploadNewMessageFile = async (file: File) => {
    if (!newMessageForm.account_id) { alert('Wybierz najpierw konto Allegro'); return; }
    setNewMessageUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('account_id', newMessageForm.account_id);
      formData.append('file', file);
      const res = await axios.post(`${API}/allegro/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewMessageAttachments(prev => [...prev, { id: res.data.attachment_id, filename: res.data.filename }]);
    } catch (e: any) {
      alert('Błąd uploadu: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setNewMessageUploadingFile(false);
  };
  const regenerateNewMessage = async () => {
    setNewMessageAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-reply`, {
        message: '(Nowa wiadomość zainicjowana przez sklep)', history: [],
        account_id: newMessageForm.account_id || '', context: '', hint: newMessageForm.text
      });
      setNewMessageForm(prev => ({ ...prev, text: res.data.draft }));
    } catch (e) { console.error(e); }
    setNewMessageAiLoading(false);
  };
  const sendNewMessage = async () => {
    if (!newMessageForm.account_id || !newMessageForm.recipient_login.trim() || !newMessageForm.order_id || !newMessageForm.text.trim()) return;
    setNewMessageSending(true);
    try {
      let textToSend = newMessageForm.text;
      if (newMessageForm.language !== 'pl') {
        const transRes = await axios.post(`${API}/ai/translate-from-polish`, {
          text: newMessageForm.text, target_lang: newMessageForm.language
        });
        textToSend = transRes.data.translation;
      }
      await axios.post(`${API}/allegro/messages/new`, {
        account_id: newMessageForm.account_id,
        recipient_login: newMessageForm.recipient_login.trim(),
        order_id: newMessageForm.order_id,
        text: textToSend,
        language: newMessageForm.language,
        attachment_ids: newMessageAttachments.map(a => a.id),
      });
      setShowNewMessage(false);
      setNewMessageForm({ account_id: '', recipient_login: '', order_id: '', text: '', language: 'pl' });
      setNewMessageOrders([]);
      setNewMessageAttachments([]);
      fetchThreads();
    } catch (e: any) {
      alert('Błąd wysyłki: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setNewMessageSending(false);
  };

  const uploadFile = async (file: File) => {
    if (!selected) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('account_id', selected.account_id || '');
      formData.append('file', file);
      const res = await axios.post(`${API}/allegro/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachments(prev => [...prev, { id: res.data.attachment_id, filename: res.data.filename }]);
    } catch (e: any) {
      alert('Błąd uploadu: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setUploadingFile(false);
  };

  const sendReply = async (learn: boolean) => {
    if (!selected || !reply.trim()) return;
    try {
      const accountsRes = await axios.get(`${API}/allegro/accounts`);
      const now = new Date();
      const expired = (accountsRes.data || []).filter((a: any) =>
        a.id === selected.account_id && new Date(a.expires_at.replace(' ', 'T') + (a.expires_at.includes('Z') || a.expires_at.includes('+') ? '' : 'Z')) < now
      );
      if (expired.length > 0) {
        setExpiredAccounts(accountsRes.data.filter((a: any) => new Date(a.expires_at.replace(' ', 'T') + (a.expires_at.includes('Z') || a.expires_at.includes('+') ? '' : 'Z')) < now).map((a: any) => a.name));
        alert(`Brak połączenia z kontem ${selected.account_name}. Odśwież połączenie w Ustawieniach.`);
        return;
      }
    } catch(e) {}
    try {
      let textToSend = reply;
      if (threadLang !== 'pl') {
        const transRes = await axios.post(`${API}/ai/translate-from-polish`, {
          text: reply, target_lang: threadLang
        });
        textToSend = transRes.data.translation;
      }
      await axios.post(`${API}/allegro/messages/${selected.id}/reply`, { text: textToSend, account_id: selected.account_id || '', attachment_ids: attachments.map(a => a.id), text_pl: threadLang !== 'pl' ? reply : '' });
      setAttachments([]);
      const clientMsgs = messages.filter(m => m.author?.login === selected.interlocutor.login);
      if (clientMsgs.length > 0) {
        const lastMsg = decodeHtml(clientMsgs[clientMsgs.length - 1].text || '');
        if (learn && replyIsManual) {
          await axios.post(`${API}/ai/approve-and-learn`, {
            question: lastMsg, answer: reply, category: classification?.category || 'ogolne',
            account_name: selected.account_name || ''
          });
        }
      }
      await axios.post(`${API}/allegro/messages/${selected.id}/mark-read`);
      setSentLearned(!!(learn && replyIsManual && clientMsgs.length > 0));
      setSent(true);
      setReply('');
      setAiDraft(null);
      setTimeout(() => { setSent(false); fetchThreads(); }, 2000);
      selectThread(selected);
    } catch (e) { console.error(e); }
  };

  const filtered = threads
    .filter(t => {
      if (filter === 'unread' && t.read) return false;
      if (filterAccount !== 'all' && t.account_id !== filterAccount) return false;
      if (search.trim()) {
        const s = search.trim().replace(/\s+/g, " ").toLowerCase();
        if (!t.interlocutor.login.toLowerCase().includes(s)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'account') {
        const accCmp = (a.account_name || '').localeCompare(b.account_name || '');
        if (accCmp !== 0) return accCmp;
      }
      return new Date(b.lastMessageDateTime).getTime() - new Date(a.lastMessageDateTime).getTime();
    });

  const paginated = filtered;
  const totalPages = Math.ceil(totalThreads / PAGE_SIZE);

  const categoryColors: any = {
    reklamacja: { bg: '#fef2f2', color: '#dc2626' },
    zwrot: { bg: '#fef2f2', color: '#dc2626' },
    zamowienie: { bg: '#eff6ff', color: '#1d4ed8' },
    dostawa: { bg: '#fefce8', color: '#ca8a04' },
    pytanie_o_produkt: { bg: '#f0fdf4', color: '#15803d' },
    inne: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const catStyle = classification ? (categoryColors[classification.category] || categoryColors.inne) : null;

  const goHome = () => {
    setShowHome(true);
    setShowOffers(false);
    setShowSettings(false);
    setShowRatings(false);
    setShowReturns(false);
    setReturnsOverdueFilter(false);
    setShowIssues(false);
    setShowRefundClaims(false);
    setShowUsers(false);
    setShowAIOpen(false);
    setShowShipments(false);
    setShowOrdersModule(false);
    setShowEmail(false);
    localStorage.removeItem('assistant_context');
  };

  const getCurrentModule = (): string => {
    const ctx = localStorage.getItem('assistant_context') || '';
    if (showHome) return 'home';
    if (showOffers) return 'offers';
    if (showApilo) return 'apilo';
    if (showSettings) return ctx.startsWith('settings:') ? ctx : 'settings';
    if (showRefundClaims) return 'refund-claims';
    if (showShipments) return 'shipments';
    if (showOrdersModule) return 'orders';
    if (showAIOpen) return 'aiopen';
    if (showRatings) return 'ratings';
    if (showReturns) return 'returns';
    if (showEmail) return ctx === 'email:thread' ? 'email:thread' : ctx === 'email:compose' ? 'email:compose' : 'email';
    if (showIssues) return ctx === 'issues:thread' ? 'issues:thread' : 'issues';
    if (ctx === 'messages:thread') return 'messages:thread';
    return 'messages';
  };

  const isFirstVisit = !localStorage.getItem('assistant_seen');

  const handleLogin = (newToken: string, user: {name: string, email: string, role: string, modules: string[]}) => {
    setToken(newToken);
    setCurrentUser(user);
    localStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken('');
    setCurrentUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const isMobile = window.innerWidth < 768;

  // Przechwytuj strzałkę wstecz i zamknięcie karty
  useEffect(() => {
    if (!token || !currentUser) return;
    // Dodaj wpis do historii żeby przechwycić popstate
    window.history.pushState(null, '', window.location.href);
    const handlePopState = (e: PopStateEvent) => {
      window.history.pushState(null, '', window.location.href);
      const confirmed = window.confirm('Czy na pewno chcesz wyjść z aplikacji OPENkontakt?');
      if (confirmed) {
        handleLogout();
        window.history.back();
      }
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Pomijamy ostrzezenie przy ZAMIERZONYM przekierowaniu (np. OAuth Allegro z kreatora).
      if ((window as any).__allowNavigation) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser]);

  // ── Panel samodzielny na subdomenie klienta (IdealnySklep) ──────────────
  // Logowanie odbywa sie na stronie SMS w katalogu panelu; tutaj tylko
  // odczytujemy zapisany token i uruchamiamy modul AI OPEN.
  const naSubdomenieKlienta = window.location.hostname.endsWith('.idealnysklep.pl');
  if (naSubdomenieKlienta) {
    const tokenKlienta = localStorage.getItem('is_token');
    if (!tokenKlienta) { window.location.href = '/'; return null; }
    const dane = (() => { try { return JSON.parse(localStorage.getItem('is_user') || '{}'); } catch { return {}; } })();
    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <AIOpen
            onBack={() => {
              localStorage.removeItem('is_token');
              localStorage.removeItem('is_user');
              window.location.href = '/';
            }}
            currentUser={{ name: dane.name, email: dane.email, role: dane.role }}
          />
        </div>
      </div>
    );
  }

  if (!token || !currentUser) return <Login onLogin={handleLogin} />;
  if (token && !userReady) return null;

  const handleModule = (module: string) => {
    setShowHome(false);
    setShowOffers(module === 'offers');
    setShowSettings(module === 'settings');
    setShowRefundClaims(module === 'refund-claims');
    setShowAIOpen(module === 'aiopen');
    setShowShipments(module === 'shipments');
    setShowOrdersModule(module === 'orders');
    setShowUsers(module === 'users');
    setShowReturns(module === 'returns');
    setShowApilo(module === 'apilo');
    setReturnsOverdueFilter(false);
    if (module === 'messages') { setShowHome(false); }
    // Ustaw kontekst asystenta przy zmianie modułu
    localStorage.setItem('assistant_context', module);
  };

  const openReturnsOverdue = () => {
    handleModule('returns');
    setReturnsOverdueFilter(true);
    setReturnsOverdueNonce(n => n + 1);
  };

  const goToReturn = (ref: string) => {
    if (!ref) return;
    handleModule('returns');
    setOpenReturnRef(ref);
    setOpenReturnNonce(n => n + 1);
  };
  const goToShipment = (orderId: string, accountId: string) => {
    if (!orderId) return;
    handleModule('shipments');
    setOpenShipmentOrderId(orderId);
    setOpenShipmentAccountId(accountId || '');
    setOpenShipmentNonce(n => n + 1);
  };
  const _assistant = <AIAssistant currentModule={getCurrentModule()} isFirstVisit={isFirstVisit} />;
  const _liveAlertBar = <LiveAlertBar currentUser={currentUser} onOpenAiOpen={() => setShowAIOpen(true)} onOpenReturns={openReturnsOverdue} />;
  if (showShipments) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'auto' }}><Shipments onBack={() => { setShowShipments(false); setShowHome(true); }} openOrderId={openShipmentOrderId} openAccountId={openShipmentAccountId} openNonce={openShipmentNonce}
 /></div>
    </div>
  );
  if (showOrdersModule) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'auto' }}><Orders onBack={() => { setShowOrdersModule(false); setShowHome(true); }}
 /></div>
    </div>
  );
  if (showAIOpen) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {(currentUser?.modules || []).includes('aiopen') ? (
          <AIOpen onBack={() => { setShowAIOpen(false); setShowHome(true); }} currentUser={currentUser} />
        ) : (
          <div style={{ padding: 40, textAlign: 'center' as const, fontFamily: 'system-ui, sans-serif' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>AI OPEN — modul niedostepny</div>
            <div style={{ fontSize: 14, color: '#6b7280', maxWidth: 460, margin: '0 auto 20px' }}>
              Asystent sprzedazy do Twojego sklepu www: znajdzie produkt, doradzi, przyjmie zamowienie.
              Ten modul nie jest aktywny na Twoim koncie.
            </div>
            <button onClick={() => { setShowAIOpen(false); setShowHome(true); }}
              style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #ddd6fe', background: '#faf5ff', color: '#7c3aed', fontWeight: 600, cursor: 'pointer' }}>
              ← Powrot do panelu
            </button>
          </div>
        )}
      </div>
    </div>
  );
  if (showUsers) return <>{_liveAlertBar}{_assistant}<Users onBack={goHome} token={token} currentUser={currentUser} /></>;
  if (showHome) return <>{_liveAlertBar}{_assistant}<Home currentUser={currentUser} onModule={handleModule} onLogout={handleLogout} expiredAccounts={expiredAccounts} modules={currentUser?.modules || []} /></>;
  if (showOffers) return <>{_liveAlertBar}{_assistant}<Offers onBack={goHome} currentUser={currentUser} /></>;
  if (showApilo) return <>{_liveAlertBar}{_assistant}<ApiloModule onBack={goHome} currentUser={currentUser} onOpenReturn={goToReturn} onOpenShipment={goToShipment} /></>;
  if (showRefundClaims) return <>{_liveAlertBar}{_assistant}<RefundClaims onBack={goHome} token={token || ''} currentUser={currentUser} /></>;
  if (showReturns) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'hidden' }}><Returns onBack={goHome} currentUser={currentUser} initialOverdueFilter={returnsOverdueFilter} overdueNonce={returnsOverdueNonce} openReturnRef={openReturnRef} openReturnNonce={openReturnNonce} /></div>
    </div>
  );
  if (showEmail) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'hidden' }}><EmailInbox onBack={() => { setShowEmail(false); localStorage.removeItem('assistant_context'); }} currentUser={currentUser} hideAlertBar={true} /></div>
    </div>
  );
  if (isMobile) return <AppMobile currentUser={currentUser} onLogout={handleLogout} onSettings={() => setShowSettings(true)} onHome={goHome} syncWarning={syncWarning} initialView={showSettings ? 'settings' : 'list'} onOpenAiOpen={() => setShowAIOpen(true)} onOpenReturns={() => { setReturnsOverdueFilter(true); setShowReturns(true); }} onOpenReturn={goToReturn} onOpenShipment={goToShipment} />;
  if (showSettings) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'hidden' }}><Settings onBack={goHome} currentUser={currentUser} hideHeader={true} /></div>
    </div>
  );
  if (showRatings) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'hidden' }}><Ratings onBack={() => { setShowRatings(false); localStorage.removeItem('assistant_context'); }} expiredAccounts={expiredAccounts} currentUser={currentUser} hideHeader={true} /></div>
    </div>
  );
  if (showIssues) return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      {_liveAlertBar}{_assistant}
      <div style={{ flex: 1, overflow: 'hidden' }}><Issues onBack={() => { setShowIssues(false); localStorage.removeItem('assistant_context'); }} expiredAccounts={expiredAccounts} currentUser={currentUser} hideHeader={true} onOpenReturn={goToReturn} onOpenShipment={goToShipment} /></div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100dvh' }}>
      <LiveAlertBar currentUser={currentUser} onOpenAiOpen={() => setShowAIOpen(true)} onOpenReturns={() => { setReturnsOverdueFilter(true); setShowReturns(true); }} />
      {((accountsLoaded && accounts.length === 0) || msgJustReturnedFromAllegro) && !allegroMsgBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby odbierać i odpowiadać na wiadomości od klientów Allegro, podłącz konto Allegro."
          returnModule="messages"
          showCongrats={msgJustReturnedFromAllegro}
          onClose={() => setAllegroMsgBannerClosed(true)}
        />
      )}
        {showOrders && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowOrders(false)}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>🛒 Zamówienia: {selected?.interlocutor.login}</div>
                <button onClick={() => setShowOrders(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
              </div>
              {loadingOrders && <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Ładowanie...</div>}
              {!loadingOrders && buyerOrders.length === 0 && (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Brak zamówień</div>
              )}
              {!loadingOrders && buyerOrders.map((o, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  {o.image && <img src={o.image} alt={o.offer_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                  {!o.image && <div style={{ width: 56, height: 56, background: '#f3f4f6', borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.offer_name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {o.sku && <span>SKU: {o.sku}</span>}
                      {o.offer_id && <span>ID: {o.offer_id}</span>}
                      <span>Ilość: {o.quantity}</span>
                      {o.bought_at && <span>{new Date(o.bought_at).toLocaleDateString('pl-PL')}</span>}
{(o as any).smart ? <span style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 4, padding: '0 6px', fontWeight: 600 }}>Smart</span> : <span style={{ color: '#9ca3af' }}>bez Smart</span>}
<OrderNoBadge o={o as any} />
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                    {o.price} {o.currency}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {showNewMessage && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowNewMessage(false)}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>✏️ Nowa wiadomość do klienta</div>
                <button onClick={() => setShowNewMessage(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
              </div>
              <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>Konto Allegro</label>
                  <select value={newMessageForm.account_id} onChange={e => { setNewMessageForm(prev => ({ ...prev, account_id: e.target.value, order_id: '' })); setNewMessageOrders([]); }}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    <option value=''>Wybierz konto...</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>Login klienta na Allegro</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={newMessageForm.recipient_login} onChange={e => setNewMessageForm(prev => ({ ...prev, recipient_login: e.target.value }))}
                      placeholder="login_klienta"
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as const }} />
                    <button onClick={fetchNewMessageOrders} disabled={newMessageLoadingOrders || !newMessageForm.account_id || !newMessageForm.recipient_login.trim()}
                      style={{ padding: '7px 14px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>
                      {newMessageLoadingOrders ? '⏳...' : 'Sprawdź zamówienia'}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Allegro wymaga wskazania zamówienia, żeby napisać do nowego klienta.</div>
                </div>
                {newMessageOrders.length > 0 && (
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>Zamówienie</label>
                    <select value={newMessageForm.order_id} onChange={e => setNewMessageForm(prev => ({ ...prev, order_id: e.target.value }))}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                      <option value=''>Wybierz zamówienie...</option>
                      {newMessageOrders.map((o: any) => (
                        <option key={o.order_id} value={o.order_id}>
                          {o.bought_at ? new Date(o.bought_at).toLocaleDateString('pl-PL') : ''} — {o.summary || o.order_id} ({o.total_items} poz.)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {newMessageOrders.length === 0 && !newMessageLoadingOrders && newMessageForm.recipient_login && (
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Kliknij "Sprawdź zamówienia", aby znaleźć zamówienia tego klienta.</div>
                )}
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>Wybierz język w jakim odbiorca otrzyma wiadomość</label>
                  <select value={newMessageForm.language} onChange={e => setNewMessageForm(prev => ({ ...prev, language: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    {[{v:'pl',l:'Polski'},{v:'en',l:'Angielski'},{v:'de',l:'Niemiecki'},{v:'fr',l:'Francuski'},{v:'cs',l:'Czeski'},{v:'sk',l:'Słowacki'},{v:'ro',l:'Rumuński'},{v:'hu',l:'Węgierski'},{v:'uk',l:'Ukraiński'},{v:'ru',l:'Rosyjski'}].map((o: any) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>Treść (pisz po polsku - odbiorca dostanie wiadomość w wybranym wyżej języku)</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <DictateButton disabled={newMessageAiLoading} onText={(t: string) => { const cur = newMessageForm.text || ''; handleNewMessageChange(cur ? cur + ' ' + t : t, (v: any) => setNewMessageForm(prev => ({ ...prev, text: v }))); }} />
                  <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <textarea ref={newMessageTextareaRef} value={newMessageForm.text}
                    onChange={e => { handleNewMessageChange(e.target.value, (v) => setNewMessageForm(prev => ({ ...prev, text: v }))); }}
                    onKeyDown={e => { if (e.key === 'Escape') setShowNewMessageSuggestions(false); }}
                    rows={6} disabled={newMessageAiLoading}
                    placeholder="Wpisz treść wiadomości po polsku... (użyj # dla skrótów)"
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', resize: 'vertical' as const, outline: 'none', boxSizing: 'border-box' as const, background: newMessageAiLoading ? '#f9fafb' : '#fff' }} />
                  {showNewMessageSuggestions && <ShortcutSuggestions suggestions={newMessageSuggestions} onSelect={s => applyNewMessageShortcut(s, newMessageForm.text, (v) => setNewMessageForm(prev => ({ ...prev, text: v })))} onClose={() => setShowNewMessageSuggestions(false)} />}
                  </div>
                  </div>
                </div>
                {newMessageAiLoading && (
                  <div style={{ padding: '7px 0', fontSize: 12, color: '#15803d' }}>⏳ AI generuje treść...</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: 12 }}>
                <label style={{ padding: '7px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                  📎 {newMessageUploadingFile ? 'Uploading...' : 'Załącz plik'}
                  <input type="file" style={{ display: 'none' }}
                    accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
                    onChange={e => { if (e.target.files?.[0]) uploadNewMessageFile(e.target.files[0]); e.target.value = ''; }}
                    disabled={newMessageUploadingFile} />
                </label>
                {newMessageAttachments.map((a, idx) => (
                  <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6, fontSize: 13, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                    📄 {a.filename}
                    <button onClick={() => setNewMessageAttachments(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
              {showNewMessageOfferPicker && (
                <OfferPicker
                  defaultAccountId={newMessageForm.account_id}
                  onClose={() => setShowNewMessageOfferPicker(false)}
                  onInsertLink={(link) => {
                    const ta = newMessageTextareaRef.current;
                    if (ta) {
                      const start = ta.selectionStart;
                      const end = ta.selectionEnd;
                      const newVal = newMessageForm.text.slice(0, start) + link + newMessageForm.text.slice(end);
                      setNewMessageForm(prev => ({ ...prev, text: newVal }));
                      setTimeout(() => { ta.focus(); ta.setSelectionRange(start + link.length, start + link.length); }, 0);
                    } else {
                      setNewMessageForm(prev => ({ ...prev, text: prev.text + link }));
                    }
                    setShowNewMessageOfferPicker(false);
                  }}
                />
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const }}>
                <button onClick={() => setShowNewMessageOfferPicker(v => !v)}
                  style={{ padding: '7px 14px', background: showNewMessageOfferPicker ? '#1d4ed8' : '#f0f9ff', color: showNewMessageOfferPicker ? '#fff' : '#0369a1', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  🏷️ Oferta
                </button>
                <button onClick={sendNewMessage} disabled={newMessageSending || !newMessageForm.order_id || !newMessageForm.text.trim()}
                  style={{ padding: '7px 16px', background: (!newMessageForm.order_id || !newMessageForm.text.trim()) ? '#f9fafb' : '#1d4ed8', color: (!newMessageForm.order_id || !newMessageForm.text.trim()) ? '#9ca3af' : '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: (!newMessageForm.order_id || !newMessageForm.text.trim()) ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                  {newMessageSending ? '⏳ Wysyłam...' : 'Wyślij'}
                </button>
                <button onClick={regenerateNewMessage} disabled={newMessageAiLoading}
                  style={{ padding: '7px 16px', background: newMessageAiLoading ? '#f9fafb' : '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, cursor: newMessageAiLoading ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                  ✦ Generuj odpowiedź AI
                </button>
                <button onClick={() => { setNewMessageForm(prev => ({ ...prev, text: '' })); setNewMessageAttachments([]); }}
                  style={{ padding: '7px 14px', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Wyczyść
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={() => setShowNewMessage(false)} style={{ padding: '7px 14px', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Wróć</button>
              </div>
            </div>
          </div>
        )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          ✓ Skopiowano: {copyToast}
        </div>
      )}
    <div style={{ display: 'flex', flex: 1, fontFamily: 'system-ui, sans-serif', fontSize: 14, minHeight: 0 }}>
      {/* SIDEBAR */}
      <div style={{ width: 300, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', background: '#f9fafb' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb' }}>
          {syncWarning && currentUser?.role === 'admin' && (
            <div style={{ background: '#fef9c3', borderBottom: '1px solid #fde68a' }}>
              <div style={{ padding: '6px 14px', color: '#92400e', fontSize: 12, fontWeight: 500 }}>
                ⚠️ Sync wiadomości nie działa — sprawdź serwer
              </div>
            </div>
          )}
          {expiredAccounts.length > 0 && (
            <div style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              <div onClick={() => { if ((currentUser?.modules||[]).includes('settings')) { setShowSettings(true); } else { alert('Brak dostępu do modułu Ustawienia. Skontaktuj się z administratorem.'); } }}
                style={{ padding: '6px 14px', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                ⚠️ Wygasłe połączenie: {expiredAccounts.join(', ')}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>✉️ Kontakt</span>
            <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => { setShowIssues(true); localStorage.setItem('assistant_context', 'issues'); }} title="Dyskusje i reklamacje"
              style={{ fontSize: 13, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>⚖️</button>
            <button onClick={() => { setShowRatings(true); localStorage.setItem('assistant_context', 'ratings'); }} title={negativeRatings > 0 ? `${negativeRatings} negatywnych ocen` : 'Oceny Allegro'}
              style={{ fontSize: 15, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                background: 'transparent', cursor: 'pointer',
                color: negativeRatings > 0 ? '#dc2626' : '#f59e0b' }}>
              ★
            </button>
            <button onClick={() => { setShowEmail(true); localStorage.setItem('assistant_context', 'email'); }} title="Skrzynka email"
              style={{ fontSize: 15, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                background: 'transparent', cursor: 'pointer', color: '#374151' }}>
              📧
            </button>
            <button onClick={() => setShowNewMessage(true)} title="Nowa wiadomość do klienta"
              style={{ fontSize: 13, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>✏️</button>
            <button onClick={goHome} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6,
              border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
              ← Wróć
            </button>
          </div>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
            👤 {currentUser?.name} · <span style={{ color: '#9ca3af' }}>{currentUser?.role}</span>
          </div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') fetchThreads(0); }}
              placeholder="Szukaj po loginie..."
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6,
                fontSize: 12, boxSizing: 'border-box', outline: 'none' }} />
            <button onClick={() => fetchThreads(0)}
              style={{ padding: '6px 10px', background: '#1d4ed8', color: '#fff', border: 'none',
                borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Szukaj
            </button>
            {search && <button onClick={() => { setSearch(''); fetchThreads(0); }}
              style={{ padding: '6px 8px', background: '#f3f4f6', color: '#6b7280', border: 'none',
                borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✕</button>}
          </div>
          <select value={filterAccount} onChange={e => { setFilterAccount(e.target.value); fetchThreads(0); }}
            style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 12, background: '#fff', marginBottom: 6 }}>
            <option value="all">Wszystkie konta</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" title="Zaznacz wszystkie na stronie"
              checked={paginated.length > 0 && paginated.every(t => selected_ids.has(t.id))}
              onChange={e => {
                if (e.target.checked) setSelectedIds(new Set([...Array.from(selected_ids), ...paginated.map(t => t.id)]));
                else { const next = new Set(Array.from(selected_ids)); paginated.forEach(t => next.delete(t.id)); setSelectedIds(next); }
              }}
              style={{ cursor: 'pointer', flexShrink: 0 }} />
            {[{k:'unread',l:'Nowe'},{k:'all',l:'Wszystkie'}].map(f => (
              <button key={f.k} onClick={() => { setFilter(f.k); setSelectedIds(new Set()); fetchThreads(0, f.k); }}
                style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid #d1d5db',
                  background: filter === f.k ? '#1d4ed8' : 'transparent',
                  color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer', flex: 1 }}>
                {f.l}
              </button>
            ))}
            <button onClick={() => fetchThreads()} title="Odśwież"
              style={{ fontSize: 12, padding: '3px 8px', borderRadius: 20, border: '1px solid #d1d5db',
                background: 'transparent', color: '#6b7280', cursor: 'pointer' }}>↻</button>
            <button title="Oznacz zaznaczone jako przeczytane"
              onClick={async () => {
                if (selected_ids.size === 0) return;
                await Promise.all(Array.from(selected_ids).map(id => axios.post(`${API}/allegro/messages/${id}/mark-read`)));
                setSelectedIds(new Set());
                fetchThreads();
              }}
              style={{ fontSize: 12, padding: '3px 8px', borderRadius: 20, border: '1px solid #d1d5db',
                background: selected_ids.size > 0 ? '#1d4ed8' : 'transparent',
                color: selected_ids.size > 0 ? '#fff' : '#9ca3af', cursor: 'pointer' }}>
              ✓
            </button>
            <AssignButton
              selectedIds={Array.from(selected_ids)}
              endpoint="/api/assign/messages"
              currentUser={currentUser}
              onAssigned={() => { setSelectedIds(new Set()); fetchThreads(); }}
              size="small"
            />
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 12 }}>Ładowanie...</div>}
          {paginated.map(t => (
            <div key={t.id}
              style={{ padding: '9px 12px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
                background: selected_ids.has(t.id) ? '#fef9c3' : selected?.id === t.id ? '#eff6ff' : !t.read ? '#fff' : '#f9fafb',
                borderLeft: selected?.id === t.id ? '3px solid #1d4ed8' : '3px solid transparent',
                display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={selected_ids.has(t.id)}
                onChange={e => {
                  e.stopPropagation();
                  const next = new Set(selected_ids);
                  if (e.target.checked) next.add(t.id); else next.delete(t.id);
                  setSelectedIds(next);
                }}
                onClick={e => e.stopPropagation()}
                style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }} />
              <div style={{ flex: 1, minWidth: 0 }} onClick={() => selectThread(t)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, alignItems: 'center' }}>
                  <span style={{ fontWeight: !t.read ? 600 : 400, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t.language && t.language !== 'pl' && <img src={`/flags/svg/${t.language}.svg`} alt={t.language} width='17' height='13' style={{verticalAlign:'middle',flexShrink:0}} />}
                    <span title="Kliknij aby skopiować" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(t.interlocutor.login); setCopyToast(t.interlocutor.login); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{t.interlocutor.login}</span>
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{formatDate(t.lastMessageDateTime)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%',
                      background: !t.read ? '#1d4ed8' : 'transparent',
                      border: '1px solid #d1d5db', display: 'inline-block', flexShrink: 0 }}></span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{!t.read ? 'Nowa' : 'Przeczytana'}</span>
                    {t.has_attachments && <span title="Załączniki od klienta" style={{ fontSize: 13 }}>📎</span>}
                    {t.language && t.language !== 'pl' && LANG_FLAGS[t.language] && (
                      <span title={`Wiadomość po: ${t.language.toUpperCase()}`} style={{ fontSize: 13 }}>{LANG_FLAGS[t.language]}</span>
                    )}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t.assigned_to && (() => { const agent = (window as any).__agents__?.find((a: any) => a.id === t.assigned_to); return agent ? <div title={agent.name} style={{ width: 8, height: 8, borderRadius: '50%', background: agent.color || '#9ca3af', flexShrink: 0 }} /> : null; })()}
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{t.account_name}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => fetchThreads(Math.max(0, page-1))} disabled={page === 0}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db',
              background: 'transparent', cursor: page === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
          <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>
            {page+1}/{Math.max(1,totalPages)} · {filtered.length} / {totalThreads} wątków
          </span>
          <button onClick={() => fetchThreads(Math.min(totalPages-1, page+1))} disabled={page >= totalPages-1}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db',
              background: 'transparent', cursor: page >= totalPages-1 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✉️</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Wybierz wiadomość</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>AI automatycznie zaproponuje odpowiedź</div>
              <div style={{ fontSize: 12, marginTop: 8, color: '#9ca3af' }}>
                {threads.length} wątków · sync co 2 min
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                Wątek z: <span title="Kliknij aby skopiować" onClick={() => { navigator.clipboard.writeText(selected.interlocutor.login); setCopyToast(selected.interlocutor.login); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{selected.interlocutor.login}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, fontSize: 12, color: '#6b7280', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{formatDateTime(selected.lastMessageDateTime)}</span>
                <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11,
                  background: !selected.read ? '#eff6ff' : '#f3f4f6',
                  color: !selected.read ? '#1d4ed8' : '#6b7280' }}>
                  {!selected.read ? 'Nieprzeczytana' : 'Przeczytana'}
                </span>
                <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, background: '#f0fdf4', color: '#15803d' }}>
                  {selected.account_name}
                </span>
                {classification && catStyle && (
                  <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11,
                    background: catStyle.bg, color: catStyle.color }}>
                    {classification.category}
                  </span>
                )}
                {!selected.read && (
                  <button onClick={async () => {
                    await axios.post(`${API}/allegro/messages/${selected.id}/mark-read`);
                    fetchThreads();
                  }} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                    border: '1px solid #d1d5db', background: 'transparent',
                    color: '#6b7280', cursor: 'pointer' }}>
                    ✓ Przeczytane
                  </button>
                )}
                <button onClick={async () => {
                  if (selected.is_handled) {
                    await axios.post(`${API}/allegro/messages/${selected.id}/unhandle`);
                    setSelected(prev => prev ? {...prev, is_handled: false, read: false} : null);
                    fetchThreads();
                  } else {
                    await axios.post(`${API}/allegro/messages/${selected.id}/handle`);
                    setSelected(null);
                    fetchThreads();
                  }
                }} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                  border: selected?.is_handled ? '1px solid #fde68a' : '1px solid #bbf7d0',
                  background: selected?.is_handled ? '#fffbeb' : '#f0fdf4',
                  color: selected?.is_handled ? '#92400e' : '#15803d', cursor: 'pointer' }}>
                  {selected?.is_handled ? '↩ Nowe' : '✓ Zakończ'}
                </button>
                {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
                  <button onClick={async () => {
                    setShowMsgHistory(h => !h);
                    if (!showMsgHistory) {
                      setMsgHistoryLoading(true);
                      const res = await axios.get(`${API}/allegro/messages/${selected.id}/history`);
                      setMsgHistory(res.data || []);
                      setMsgHistoryLoading(false);
                    }
                  }} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                    border: '1px solid #e5e7eb', background: showMsgHistory ? '#f3f4f6' : '#fff',
                    color: '#6b7280', cursor: 'pointer' }}>
                    🕐 Historia
                  </button>
                )}
                <button onClick={fetchBuyerOrders} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                  border: '1px solid #bfdbfe', background: '#eff6ff',
                  color: '#1d4ed8', cursor: 'pointer' }}>
                  🛒 Zamówienia
                </button>
                <BuyerReturnsButton buyerLogin={selected.interlocutor.login} onOpenReturn={goToReturn} />
                <BuyerShipmentsButton buyerLogin={selected.interlocutor.login} onOpenShipment={goToShipment} />
                <button onClick={async () => {
                  const res = await axios.post(`${API}/allegro/messages/${selected.id}/toggle-auto-reply`);
                  setSelected({...selected, auto_reply_disabled: res.data.auto_reply_disabled});
                }} style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10,
                  border: selected.auto_reply_disabled ? '1px solid #fca5a5' : '1px solid #e5e7eb',
                  background: selected.auto_reply_disabled ? '#fef2f2' : 'transparent',
                  color: selected.auto_reply_disabled ? '#dc2626' : '#6b7280', cursor: 'pointer' }}>
                  {selected.auto_reply_disabled ? '🚫 Auto-odp. wyłączona' : '🤖 Auto-odp. włączona'}
                </button>
                {threadLang !== 'pl' && (
                  <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11,
                    background: '#EEEDFE', color: '#3C3489', fontWeight: 500 }}>
                    🌐 {threadLang.toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {showMsgHistory && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
              <div style={{ padding: '8px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>
                {msgHistoryLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
                {!msgHistoryLoading && msgHistory.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
                {msgHistory.map((h, i) => (
                  <div key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 4, marginBottom: 4, borderBottom: i < msgHistory.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                    <span>{h.event === 'read' ? '👁' : h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}</span>
                    <div>
                      <span style={{ fontWeight: 500, color: '#374151' }}>
                        {h.event === 'read' ? 'Oznaczono jako przeczytane' : h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `Przydzielono do: ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Wysłano odpowiedź' : h.event}
                      </span>
                      <span style={{ color: '#9ca3af', marginLeft: 6 }}>{h.actor_name}</span>
                      <div style={{ color: '#9ca3af', fontSize: 11 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ flex: 1, padding: '14px 18px', overflowY: 'auto', background: '#f9fafb' }}>
            {loadingMessages && <div style={{ color: '#6b7280', fontSize: 12, textAlign: 'center', padding: 20 }}>Ładowanie wiadomości...</div>}
              {offerInfo && (
              <div style={{ margin: '0 0 12px 0', padding: '10px 14px', background: '#fff',
                border: '1px solid #e5e7eb', borderRadius: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                {offerInfo.image && (
                  <img src={offerInfo.image} alt={offerInfo.name}
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Pytanie dotyczy oferty:</div>
                  <a href={offerInfo.url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 13, fontWeight: 500, color: '#1d4ed8', textDecoration: 'none',
                      display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {offerInfo.name}
                  </a>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>#{offerInfo.id}</div>
                </div>
              </div>
            )}
            {messages.map(m => {
                const isClient = m.author?.login === selected.interlocutor.login;
                return (
                  <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column',
                    alignItems: isClient ? 'flex-start' : 'flex-end' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>
                      {m.author?.login} · {formatDateTime(m.createdAt)}
                    </div>
                    <div style={{ maxWidth: '72%', padding: '9px 13px', borderRadius: 12, fontSize: 13, lineHeight: 1.6,
                      background: isClient ? '#fff' : '#1d4ed8',
                      color: isClient ? '#374151' : '#fff',
                      border: isClient ? '1px solid #e5e7eb' : 'none' }}>
                      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{decodeHtml(m.text || '')}</span>
                      {isClient && threadLang !== 'pl' && (
                        <div style={{ marginTop: 6, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
                          {showTranslation[m.id] ? (
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>🇵🇱 Tłumaczenie:</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>
                                {translations[m.id] || '⏳ Tłumaczę...'}
                              </div>
                              <button onClick={() => setShowTranslation(prev => ({...prev, [m.id]: false}))}
                                style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 }}>
                                Ukryj tłumaczenie
                              </button>
                            </div>
                          ) : (
                            <button onClick={async () => {
                              setShowTranslation(prev => ({...prev, [m.id]: true}));
                              if (!translations[m.id]) {
                                const res = await axios.post(`${API}/ai/translate-to-polish`, {
                                  text: decodeHtml(m.text || ''), source_lang: threadLang
                                });
                                setTranslations(prev => ({...prev, [m.id]: res.data.translation}));
                              }
                            }} style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              🇵🇱 Pokaż tłumaczenie
                            </button>
                          )}
                        </div>
                      )}
                      {!isClient && threadLang !== 'pl' && (
                        <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 6 }}>
                          {showTranslation[m.id] ? (
                            <div>
                              <div style={{ fontSize: 11, color: '#bfdbfe', marginBottom: 3 }}>🇵🇱 Wersja polska:</div>
                              <div style={{ fontSize: 13, color: '#fff' }}>
                                {translations[m.id] || '⏳ Tłumaczę...'}
                              </div>
                              <button onClick={() => setShowTranslation(prev => ({...prev, [m.id]: false}))}
                                style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 }}>
                                Ukryj
                              </button>
                            </div>
                          ) : (
                            <button onClick={async () => {
                              setShowTranslation(prev => ({...prev, [m.id]: true}));
                              if (!translations[m.id]) {
                                const res = await axios.post(`${API}/ai/translate-to-polish`, {
                                  text: decodeHtml(m.text || ''), source_lang: threadLang
                                });
                                setTranslations(prev => ({...prev, [m.id]: res.data.translation}));
                              }
                            }} style={{ fontSize: 11, color: '#bfdbfe', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              🇵🇱 Pokaż po polsku
                            </button>
                          )}
                        </div>
                      )}
                      {m.attachments && m.attachments.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {m.attachments.map((att: any, idx: number) => (
                            <div key={idx}>
                              {att.mimeType && att.mimeType.startsWith('image/') ? (
                                <a href={`/api${att.proxy_url}&token=${getAuthToken()}`} target="_blank" rel="noreferrer">
                                  <img src={`/api${att.proxy_url}&token=${getAuthToken()}`} alt={att.fileName}
                                    style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8,
                                      border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'block' }} />
                                </a>
                              ) : (
                                <a href={`/api${att.proxy_url}&token=${getAuthToken()}`} target="_blank" rel="noreferrer"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                                    fontSize: 12, padding: '5px 10px', borderRadius: 6, textDecoration: 'none',
                                    background: isClient ? '#f0f9ff' : 'rgba(255,255,255,0.2)',
                                    color: isClient ? '#0369a1' : '#fff',
                                    border: isClient ? '1px solid #bae6fd' : '1px solid rgba(255,255,255,0.3)' }}>
                                  {att.mimeType && att.mimeType.startsWith('video/') ? '🎥' : '📎'}
                                  {att.fileName || 'Zalacznik'} ↗
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {lockInfo && (
                <div style={{ margin: '10px 0', padding: '10px 14px', background: '#fef9c3',
                  border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                  ⚠️ {lockInfo} – możesz przeglądać ale uważaj na konflikty
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff' }}>
              {aiLoading && (
                <div style={{ padding: '7px 18px', background: '#f0fdf4', fontSize: 12, color: '#15803d' }}>
                  ⏳ AI generuje odpowiedź...
                </div>
              )}
              {aiDraft && !aiLoading && (
                <div style={{ padding: '6px 18px', background: '#f0fdf4', fontSize: 12, color: '#15803d',
                  display: 'flex', justifyContent: 'space-between' }}>
                  <span>✦ Propozycja AI · Pewność: <strong>{aiDraft.confidence}%</strong> · Edytuj przed wysyłką</span>
                </div>
              )}
              <div style={{ padding: '12px 18px' }}>
                {sent && (
                  <div style={{ padding: '7px 12px', background: '#f0fdf4', color: '#15803d',
                    borderRadius: 6, fontSize: 12, marginBottom: 8 }}>
                    {sentLearned ? '✓ Odpowiedź wysłana i zapisana do bazy wiedzy AI' : '✓ Odpowiedź wysłana'}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
                  <span>🌐 Klient otrzyma odpowiedź w języku:</span>
                  <select value={threadLang} onChange={e => { setThreadLang(e.target.value); setReplyTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                    {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map((o: any) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </div>
                {replyTranslation && (
                  <div style={{ padding: '8px 12px', background: '#EEEDFE', borderRadius: 6,
                    fontSize: 13, color: '#3C3489', marginBottom: 8, lineHeight: 1.6 }}>
                    <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 4, fontWeight: 500 }}>
                      🌐 Tłumaczenie do wysłania ({threadLang.toUpperCase()}):
                    </div>
                    {replyTranslation}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <DictateButton disabled={aiLoading} onText={(t: string) => { const cur = reply || ''; handleReplyChange(cur ? cur + ' ' + t : t, (v: any) => { setReply(v); setReplyIsManual(true); }); }} />
                <div ref={replyRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <textarea ref={replyTextareaRef} value={reply} onChange={e => { handleReplyChange(e.target.value, (v) => { setReply(v); setReplyIsManual(true); }); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,400)+'px';}}
                  onKeyDown={e => { if (e.key === 'Escape') setShowReplySuggestions(false); }}
                  placeholder="Wpisz odpowiedź po polsku..."
                  disabled={aiLoading}
                  style={{ width: '100%', minHeight: 180, padding: '9px 12px',
                    border: `1px solid ${aiDraft ? '#bbf7d0' : '#d1d5db'}`,
                    borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', resize: 'vertical',
                    outline: 'none', boxSizing: 'border-box', background: aiLoading ? '#f9fafb' : '#fff' }} />
                {showReplySuggestions && <ShortcutSuggestions suggestions={replySuggestions} onSelect={s => applyReplyShortcut(s, reply, (v) => { setReply(v); setReplyIsManual(true); })} onClose={() => setShowReplySuggestions(false)} />}
                </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                    <label style={{ padding: '7px 16px', background: '#f3f4f6', border: '1px solid #d1d5db',
                      borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                      📎 {uploadingFile ? 'Uploading...' : 'Załącz plik'}
                      <input type="file" style={{ display: 'none' }}
                        accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf"
                        onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
                        disabled={uploadingFile} />
                    </label>
                    {attachments.map((a, idx) => (
                      <span key={idx} style={{ padding: '3px 8px', background: '#dbeafe', borderRadius: 6,
                        fontSize: 13, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📄 {a.filename}
                        <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: 0 }}>✕</button>
                      </span>
                    ))}
                  </div>
                {selected && isAccountExpired(selected?.account_name || '') && (
                  <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 6, fontSize: 12,
                    color: '#dc2626', marginBottom: 8, border: '1px solid #fecaca' }}>
                    ⚠️ Brak połączenia z kontem {selected.account_name} – nie można wysłać odpowiedzi.
                  </div>
                )}
                  {showOfferPicker && (
                    <OfferPicker
                      defaultAccountId={selected?.account_id}
                      onClose={() => setShowOfferPicker(false)}
                      onInsertLink={(link) => {
                        const ta = replyTextareaRef.current;
                        if (ta) {
                          const start = ta.selectionStart;
                          const end = ta.selectionEnd;
                          const newVal = reply.slice(0, start) + link + reply.slice(end);
                          setReply(newVal);
                          setReplyIsManual(true);
                          setTimeout(() => { ta.focus(); ta.setSelectionRange(start + link.length, start + link.length); }, 0);
                        } else {
                          setReply(prev => prev + link);
                        }
                        setShowOfferPicker(false);
                      }}
                    />
                  )}
                  <button onClick={() => setShowOfferPicker(v => !v)}
                    style={{ padding: '7px 14px', background: showOfferPicker ? '#1d4ed8' : '#f0f9ff',
                      color: showOfferPicker ? '#fff' : '#0369a1', border: '1px solid #bae6fd',
                      borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                    🏷️ Oferta
                  </button>
                  <button onClick={() => sendReply(false)} disabled={aiLoading || !reply.trim() || isAccountExpired(selected?.account_name || "")}
                    style={{ padding: '7px 16px',
                      background: !reply.trim() ? '#9ca3af' : '#1d4ed8',
                      color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
                      cursor: !reply.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                    Wyślij
                  </button>
                  <button onClick={() => sendReply(true)} disabled={aiLoading || !reply.trim() || isAccountExpired(selected?.account_name || "")}
                    style={{ padding: '7px 16px',
                      background: !reply.trim() ? '#f9fafb' : '#f0fdf4',
                      color: !reply.trim() ? '#9ca3af' : '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13,
                      cursor: !reply.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                    Wyślij i ucz AI
                  </button>
                  <button onClick={regenerateReply} disabled={aiLoading}
                    style={{ padding: '7px 16px',
                      background: aiLoading ? '#f9fafb' : '#f0fdf4',
                      color: '#15803d', border: '1px solid #bbf7d0',
                      borderRadius: 6, fontSize: 13, cursor: aiLoading ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                    ✦ Generuj odpowiedź AI
                  </button>
                  {threadLang !== 'pl' && reply.trim() && (
                    <button onClick={async () => {
                      setTranslatingReply(true);
                      const res = await axios.post(`${API}/ai/translate-from-polish`, {
                        text: reply, target_lang: threadLang
                      });
                      setReplyTranslation(res.data.translation);
                      setTranslatingReply(false);
                    }} disabled={translatingReply}
                      style={{ padding: '7px 14px', background: '#EEEDFE', color: '#3C3489',
                        border: '1px solid #C4C2F5', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                      {translatingReply ? '⏳ Tłumaczę...' : `🌐 Podgląd ${threadLang.toUpperCase()}`}
                    </button>
                  )}
                  <button onClick={() => { setReply(''); setReplyTranslation(''); }}
                    style={{ padding: '7px 14px', background: 'transparent', color: '#6b7280',
                      border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                    Wyczyść
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    <AIAssistant currentModule={getCurrentModule()} isFirstVisit={isFirstVisit} />
    </div>
  );
}

export default App;
